import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import {
  bibleBooks,
  chapterCount,
  formatScriptureRef,
  parseScriptureRef,
  verseCount,
  type BibleBookId,
  type ScriptureRefParts,
} from '@/constants/bible';
import { cn } from '@/utils/cn';

export interface ScriptureRefPickerLabels {
  book: string;
  chapter: string;
  verse: string;
  verseEnd?: string;
  bookPlaceholder: string;
  chapterPlaceholder: string;
  versePlaceholder: string;
  verseEndPlaceholder?: string;
  bookEmpty?: string;
  searchPlaceholder?: string;
  clearLabel?: string;
  testamentOld: string;
  testamentNew: string;
}

/** Labels from the `content` namespace for console scripture pickers. */
export const scriptureRefPickerLabels = (
  t: TFunction<'content'>,
  clearLabel: string,
): ScriptureRefPickerLabels => ({
  book: t('scripture.book'),
  chapter: t('scripture.chapter'),
  verse: t('scripture.verse'),
  verseEnd: t('scripture.verseEnd'),
  bookPlaceholder: t('scripture.bookPlaceholder'),
  chapterPlaceholder: t('scripture.chapterPlaceholder'),
  versePlaceholder: t('scripture.versePlaceholder'),
  verseEndPlaceholder: t('scripture.verseEndPlaceholder'),
  bookEmpty: t('scripture.bookEmpty'),
  searchPlaceholder: t('scripture.searchPlaceholder'),
  clearLabel,
  testamentOld: t('scripture.testamentOld'),
  testamentNew: t('scripture.testamentNew'),
});

export interface ScriptureRefPickerProps {
  /** Canonical reference string (`John 3:16` or `John 3:16–18`), or empty. */
  value: string;
  onChange: (value: string) => void;
  labels: ScriptureRefPickerLabels;
  /** When true, show an optional end-verse dropdown for ranges. */
  allowRange?: boolean;
  disabled?: boolean;
  id?: string;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  size?: 'sm' | 'md';
}

interface Draft {
  bookId: BibleBookId | null;
  chapter: number | null;
  verse: number | null;
  verseEnd: number | null;
}

const emptyDraft = (): Draft => ({
  bookId: null,
  chapter: null,
  verse: null,
  verseEnd: null,
});

const draftFromValue = (value: string): Draft => {
  const parsed = parseScriptureRef(value);
  if (!parsed) return emptyDraft();
  return {
    bookId: parsed.bookId,
    chapter: parsed.chapter,
    verse: parsed.verse,
    verseEnd: parsed.verseEnd,
  };
};

const emitValue = (draft: Draft): string => {
  if (draft.bookId == null || draft.chapter == null || draft.verse == null) return '';
  const parts: ScriptureRefParts = {
    bookId: draft.bookId,
    chapter: draft.chapter,
    verse: draft.verse,
    verseEnd:
      draft.verseEnd != null && draft.verseEnd > draft.verse ? draft.verseEnd : null,
  };
  return formatScriptureRef(parts);
};

/**
 * Cascading book → chapter → verse (optional end verse) picker. Emits a canonical
 * scripture string when book, chapter and verse are all set; clears when incomplete.
 */
export const ScriptureRefPicker = ({
  value,
  onChange,
  labels,
  allowRange = false,
  disabled,
  id,
  popoverPortal = 'body',
  className,
  size = 'sm',
}: ScriptureRefPickerProps) => {
  const { t: tCommon } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => draftFromValue(value));

  // Sync from the parent when the stored string changes externally (open edit / reset).
  // Do not wipe an in-progress book→chapter→verse selection that has not emitted yet.
  useEffect(() => {
    setDraft((current) => {
      const emitted = emitValue(current);
      if (value === emitted) return current;
      if (value === '' && emitted === '') return current;
      return draftFromValue(value);
    });
  }, [value]);

  const bookOptions = useMemo(
    () =>
      bibleBooks.map((book) => ({
        value: book.id,
        label: book.name,
        group: book.testament === 'ot' ? labels.testamentOld : labels.testamentNew,
        keywords: [...book.aliases],
      })),
    [labels.testamentNew, labels.testamentOld],
  );

  const chapterOptions = useMemo(() => {
    if (!draft.bookId) return [];
    const count = chapterCount(draft.bookId);
    return Array.from({ length: count }, (_, index) => {
      const chapter = index + 1;
      return { value: String(chapter), label: String(chapter) };
    });
  }, [draft.bookId]);

  const verseOptions = useMemo(() => {
    if (!draft.bookId || draft.chapter == null) return [];
    const count = verseCount(draft.bookId, draft.chapter);
    return Array.from({ length: count }, (_, index) => {
      const verse = index + 1;
      return { value: String(verse), label: String(verse) };
    });
  }, [draft.bookId, draft.chapter]);

  const verseEndOptions = useMemo(() => {
    if (!draft.bookId || draft.chapter == null || draft.verse == null) return [];
    const count = verseCount(draft.bookId, draft.chapter);
    const options = [];
    for (let verse = draft.verse + 1; verse <= count; verse += 1) {
      options.push({ value: String(verse), label: String(verse) });
    }
    return options;
  }, [draft.bookId, draft.chapter, draft.verse]);

  const update = (next: Draft) => {
    setDraft(next);
    const nextValue = emitValue(next);
    if (nextValue !== '') {
      onChange(nextValue);
      return;
    }
    // Incomplete selection: clear a previously saved ref, but do not notify on every
    // intermediate pick (book alone) when nothing was stored yet.
    if (value !== '') onChange('');
  };

  const clearLabel = labels.clearLabel ?? tCommon('action.clearSelection');
  const bookId = id ? `${id}-book` : undefined;
  const chapterId = id ? `${id}-chapter` : undefined;
  const verseId = id ? `${id}-verse` : undefined;
  const verseEndId = id ? `${id}-verse-end` : undefined;

  return (
    <div
      className={cn(
        'grid gap-2',
        allowRange ? 'sm:grid-cols-4' : 'sm:grid-cols-3',
        className,
      )}
    >
      <div>
        <Label htmlFor={bookId} className="text-caption text-fg-secondary">
          {labels.book}
        </Label>
        <Listbox<BibleBookId>
          id={bookId}
          value={draft.bookId}
          onChange={(bookIdNext) => {
            update({
              bookId: bookIdNext,
              chapter: null,
              verse: null,
              verseEnd: null,
            });
          }}
          options={bookOptions}
          placeholder={labels.bookPlaceholder}
          searchPlaceholder={labels.searchPlaceholder}
          emptyMessage={labels.bookEmpty}
          searchable
          clearable
          clearLabel={clearLabel}
          disabled={disabled}
          size={size}
          popoverPortal={popoverPortal}
        />
      </div>

      <div>
        <Label htmlFor={chapterId} className="text-caption text-fg-secondary">
          {labels.chapter}
        </Label>
        <Listbox
          id={chapterId}
          value={draft.chapter != null ? String(draft.chapter) : null}
          onChange={(chapterValue) => {
            const chapter = chapterValue != null ? Number(chapterValue) : null;
            update({
              ...draft,
              chapter,
              verse: null,
              verseEnd: null,
            });
          }}
          options={chapterOptions}
          placeholder={labels.chapterPlaceholder}
          disabled={disabled || !draft.bookId}
          clearable
          clearLabel={clearLabel}
          searchable={chapterOptions.length > 12}
          searchPlaceholder={labels.searchPlaceholder}
          size={size}
          popoverPortal={popoverPortal}
        />
      </div>

      <div>
        <Label htmlFor={verseId} className="text-caption text-fg-secondary">
          {labels.verse}
        </Label>
        <Listbox
          id={verseId}
          value={draft.verse != null ? String(draft.verse) : null}
          onChange={(verseValue) => {
            const verse = verseValue != null ? Number(verseValue) : null;
            update({
              ...draft,
              verse,
              verseEnd:
                draft.verseEnd != null && verse != null && draft.verseEnd > verse
                  ? draft.verseEnd
                  : null,
            });
          }}
          options={verseOptions}
          placeholder={labels.versePlaceholder}
          disabled={disabled || draft.chapter == null}
          clearable
          clearLabel={clearLabel}
          searchable={verseOptions.length > 12}
          searchPlaceholder={labels.searchPlaceholder}
          size={size}
          popoverPortal={popoverPortal}
        />
      </div>

      {allowRange ? (
        <div>
          <Label htmlFor={verseEndId} className="text-caption text-fg-secondary">
            {labels.verseEnd ?? labels.verse}
          </Label>
          <Listbox
            id={verseEndId}
            value={draft.verseEnd != null ? String(draft.verseEnd) : null}
            onChange={(verseEndValue) => {
              const verseEnd = verseEndValue != null ? Number(verseEndValue) : null;
              update({
                ...draft,
                verseEnd,
              });
            }}
            options={verseEndOptions}
            placeholder={labels.verseEndPlaceholder ?? labels.versePlaceholder}
            disabled={disabled || draft.verse == null || verseEndOptions.length === 0}
            clearable
            clearLabel={clearLabel}
            searchable={verseEndOptions.length > 12}
            searchPlaceholder={labels.searchPlaceholder}
            size={size}
            popoverPortal={popoverPortal}
          />
        </div>
      ) : null}
    </div>
  );
};
